import i18next from "i18n/i18n";
import {
  Alignment,
  Autoformat,
  AutoLink,
  AutoMediaEmbed,
  Autosave,
  BlockQuote,
  Bold,
  Bookmark,
  Clipboard,
  ClipboardPipeline,
  CloudServices,
  DragDrop,
  Enter,
  FindAndReplace,
  Font,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  IconObjectCenter,
  IconObjectLeft,
  IconObjectRight,
  Image,
  ImageBlock,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  List,
  ListProperties,
  MediaEmbed,
  MediaEmbedToolbar,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  PastePlainText,
  RemoveFormat,
  SelectAll,
  ShiftEnter,
  ShowBlocks,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TodoList,
  Typing,
  Underline,
  Undo,
} from "ckeditor5";
import {
  Comments,
  ExportPdf,
  ImportWord,
  PasteFromOfficeEnhanced,
  PresenceList,
  RealTimeCollaborativeComments,
  RealTimeCollaborativeEditing,
  RealTimeCollaborativeRevisionHistory,
  RealTimeCollaborativeTrackChanges,
  RevisionHistory,
  TrackChanges,
} from "ckeditor5-premium-features";
import Messenger from "messaging/messenger";
import Backbone from "backbone";
import Marionette from "marionette";
import { WProofreader } from "@webspellchecker/wproofreader-ckeditor5";

import CkEditor4Conversion from "./ckEditor4Conversion/ckEditor4Conversion";
import Accordion from "./accordion/accordion";
import AkLink from "./akLink/akLink";
import AkLinkImage from "./akLink/akLinkImage";
import { AkCommentsAdapter } from "./comments/akCommentsAdapter";
import AkConfig from "./config/AkConfig";
import AkUploadAdapter from "./uploadAdapter/akUploadAdapter";
import CkEditor5View from "./ckEditor5View.js";
import CollapsibleSection from "./collapsibleSection/collapsibleSection";
import ConversationVariable from "./conversationVariable/conversationVariable";
import CustomTemplate from "./customTemplate/customTemplate";
import FigureTools from "./figureTools/figureTools";
import CloudModel from "./cloud/cloudModel";
import "modules/header/kbMenu/kbMenuModel";
import MessengerAdapter from "./messenger/messengerAdapter";
import Preview from "./preview/preview";
import PrivilegedArea from "./privilegedArea/privilegedArea";
import Properties from "configuration/properties";
import ResponseAttribute from "./responseAttribute/responseAttribute";
import ResponseCategory from "./responseCategory/responseCategory";
import SpecialCharactersExtended from "./specialCharactersExtended/specialCharactersExtended";
import Snippet from "./snippet/snippet";
import "modules/topicsSplit/snippet/snippetModel";
import TopicResponseModel from "modules/topicsSplit/topic/topicResponse/topicResponseModel";
import ResponseTransformation from "./responseTransformation/responseTransformation";
import ExpandResponseWithContext from "./responseTransformation/expandResponseWithContext";
import SummarizeResponse from "./responseTransformation/summarizeResponse";
import AiResponseTransformationOperationsModel from "./responseTransformation/aiResponseTransformationOperationsModel";

import coreTranslationsDe from "ckeditor5/translations/de";
import coreTranslationsEn from "ckeditor5/translations/en";
import coreTranslationsEs from "ckeditor5/translations/es";
import coreTranslationsFr from "ckeditor5/translations/fr";
import coreTranslationsZhCn from "ckeditor5/translations/zh-cn";
import premiumFeaturesTranslationsDe from "ckeditor5-premium-features/translations/de";
import premiumFeaturesTranslationsEn from "ckeditor5-premium-features/translations/en";
import premiumFeaturesTranslationsEs from "ckeditor5-premium-features/translations/es";
import premiumFeaturesTranslationsFr from "ckeditor5-premium-features/translations/fr";
import premiumFeaturesTranslationsZhCn from "ckeditor5-premium-features/translations/zh-cn";

import "ckeditor5/ckeditor5.css";
import "ckeditor5-premium-features/ckeditor5-premium-features.css";
import "@webspellchecker/wproofreader-ckeditor5/index.css";

const LINK_REL_SAFE = "noopener noreferrer";
const LINK_TARGET_BLANK = "_blank";

const CKEditor5Module = {
  ckEditor5Module: Marionette.Controller.extend({
    ckEditorUsers: null,
    dataChanged: false,
    editorConfig: null,
    model: null,
    view: null,
    initialize: function (options) {
      this.region = options.region;
      this.name = "ckEditor5";
      this.namespace = options.namespace;
      this.dataChanged = false;
      this._setUpReqres();
    },
    show: async function (options) {
      options = options || {};

      const tenantId = Messenger.reqres.request("active:tenantId");
      this.ckEditorUsers = Messenger.reqres.request("tenant:and:global:users", tenantId);
      options.aiOperations = await this._loadAiOperations();
      options.response = this.prepareResponseForEdit(options.response);
      const plugins = preparePlugins(options);
      const editorConfig = prepareDefaultConfig(options);
      const allOptions = {
        ...options,
        plugins,
        ...editorConfig,
      };
      this.model = new Backbone.Model(allOptions);
      this.dataChanged = false;
      // noinspection JSPotentiallyInvalidConstructorUsage
      this.view = new CkEditor5View.ckEditor5View({ model: this.model });
      this.view.on("change:data", () => (this.dataChanged = true));
      this.view.on("editor:initialized", () => this.trigger("editor:initialized"));
      this.view.on("editor:initializeError", (error) => {
        const message = error.toString();
        if (this.model.get("collaborationEnabled")) {
          this._showInitializeError(message);
        } else {
          Messenger.okDialog([i18next.t("ckeditor.initializeError"), message], {
            ok: () =>
              Messenger.vent.trigger(
                Messenger.getFullName("ckeditor:initializeError", this.namespace),
              ),
          });
        }
      });

      if (editorConfig.collaborationEnabled) {
        try {
          let commentThreadsSyncPromise;
          if (options.commentThreads) {
            commentThreadsSyncPromise = options.commentThreads.onSyncedPromise();
          } else {
            commentThreadsSyncPromise = Promise.resolve();
          }

          await commentThreadsSyncPromise;

          const documentId = this.model.get("documentId");
          const responseId = this.model.get("responseId");

          const cloudDocument = await this._importCkEditorCloudDocumentIfNeeded(
            documentId,
            responseId,
            this.model.get("response"),
            this.model.get("ckCloudDocumentVersion"),
          );

          if (cloudDocument) {
            const updatedVersion = cloudDocument.get("respTempVersion");
            if (updatedVersion) {
              this.model.set("version", updatedVersion);
            }
          }

          this.showRegion(this.view);
        } catch (error) {
          const message = error.toString();
          this._showInitializeError(message);
        }
      } else {
        // areas like variable prompt and response translation editor do not use collaboration
        // features, so just show the response without importing and creating a collab session.
        this.showRegion(this.view);
      }
    },
    isCollaborationEnabled: function () {
      return this.model && this.model.get("collaborationEnabled");
    },
    isDirty: function () {
      this.view.commitData();
      return this.dataChanged;
    },
    getResponse: function () {
      let response = this.view.getResponse();
      return TopicResponseModel.responseTemplate.prepareResponseForSave(response);
    },
    getResponseWithoutTags: function () {
      return this.view.getResponse();
    },
    getResponseVersion: function () {
      return this.model.get("version") || 1;
    },
    getCloudDocumentVersion: function () {
      return this.view.getCloudDocumentVersion();
    },
    prepareResponseForEdit: function (response) {
      return TopicResponseModel.responseTemplate.prepareResponseForEdit(response);
    },
    _importCkEditorCloudDocumentIfNeeded: function (
      documentId,
      responseId,
      responseText,
      ckCloudDocumentVersion,
    ) {
      const tokenUrl = Messenger.reqres.request("get:ckeditor:tokenUrl");
      if (!tokenUrl || !documentId) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const topicId = Messenger.reqres.request("current:topic:id");
        const ckEditorCloudDocumentModel = new CloudModel.importDocument({
          intentId: topicId,
          documentId: documentId,
          responseId: responseId,
        });
        ckEditorCloudDocumentModel.set(
          "content",
          this._prepareCkDocumentContentForImport(responseText, ckCloudDocumentVersion),
        );
        ckEditorCloudDocumentModel.save(null, {
          success: () => resolve(ckEditorCloudDocumentModel),
          error: (_model, response) => reject(Messenger.getErrorFromResponse(response)),
        });
      });
    },
    _prepareCkDocumentContentForImport: function (responseText, ckCloudDocumentVersion) {
      const bundleVersion = Messenger.reqres.request("get:ckeditor:bundleVersion");
      return {
        bundle_version: bundleVersion, // bundleVersion should be set to CkEditor version when an editor bundle is disabled
        data: responseText,
        version: ckCloudDocumentVersion,
        use_initial_data: false,
      };
    },
    _loadAiOperations: async function () {
      const operationsModel =
        new AiResponseTransformationOperationsModel.responseTransformationOperations();
      try {
        operationsModel.fetch();
        await operationsModel.onSyncedPromise();
        return operationsModel.map((operation) => ({
          name: operation.get("name"),
          displayName: operation.get("displayName"),
        }));
      } catch (error) {
        console.error("Failed to load AI operations:", error);
        return [];
      }
    },
    getSessionConnectedUsers: function (documentId, responseId) {
      const tokenUrl = Messenger.reqres.request("get:ckeditor:tokenUrl");
      if (!tokenUrl || !documentId) {
        return Promise.resolve([]);
      }
      const topicId = Messenger.reqres.request("current:topic:id");
      const ckEditorCloudDocumentModel = new CloudModel.connectedUsers({
        intentId: topicId,
        documentId: documentId,
        responseId: responseId,
      });
      ckEditorCloudDocumentModel.fetch();
      return ckEditorCloudDocumentModel
        .onSyncedPromise()
        .then((model) => model.get("connectedUsers"));
    },
    _showInitializeError: function (message) {
      Messenger.yesNoDialog([i18next.t("ckeditor.initializeErrorConfirm"), message], {
        yes: () => {
          Messenger.vent.trigger(Messenger.getFullName("ckeditor:deleteDocument", this.namespace));
          Messenger.vent.trigger(Messenger.getFullName("ckeditor:initializeError", this.namespace));
        },
        no: () =>
          Messenger.vent.trigger(Messenger.getFullName("ckeditor:initializeError", this.namespace)),
      });
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler(
        "response:commentThreads",
        () => this.model.get("commentThreads"),
        this,
      );
      Messenger.reqres.setHandler("editor:response:data", () => this.model.get("response"), this);
      Messenger.reqres.setHandler("editor:users", () => this.ckEditorUsers, this);
      Messenger.reqres.setHandler(
        "editor:displayMode:wideSidebar",
        (annotationsUIs) => this.view.activateBothInlineModeAndWideSidebarMode(annotationsUIs),
        this,
      );
      Messenger.reqres.setHandler(
        "editor:displayMode:inline",
        (annotationsUIs) => this.view.switchDisplayModeToInline(annotationsUIs),
        this,
      );
    },
  }),
};

function preparePlugins(options) {
  // NOTE: Plug-ins added here might need to also be added to ckeditorCs.js
  // See https://ckeditor.com/docs/cs/latest/guides/collaboration/editor-bundle.html#building-the-editor-bundle
  const plugins = [
    MessengerAdapter,
    Accordion,
    Alignment,
    AkConfig,
    AkUploadAdapter,
    Autoformat,
    AutoLink,
    AutoMediaEmbed,
    Bold,
    Bookmark,
    BlockQuote,
    Clipboard,
    ClipboardPipeline,
    CollapsibleSection,
    ConversationVariable,
    CustomTemplate,
    ...(options.skipImage ? [] : [DragDrop]),
    Enter,
    FindAndReplace,
    Font,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    Image, // cannot skip Image as it is required as a dependency for AkLinkImage
    // Image plugins are conditionally added here
    ...(options.skipImage
      ? []
      : [
          ImageBlock,
          ImageCaption,
          ImageResize,
          ImageInsert,
          ImageStyle,
          ImageToolbar,
          ImageUpload,
        ]),
    Indent,
    IndentBlock,
    Italic,
    // These must come after Image
    AkLink,
    AkLinkImage,
    CkEditor4Conversion,
    List,
    ListProperties,
    MediaEmbed,
    MediaEmbedToolbar,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    PastePlainText,
    Preview,
    PrivilegedArea,
    RemoveFormat,
    ResponseAttribute,
    ResponseCategory,
    SelectAll,
    ShiftEnter,
    ShowBlocks,
    Snippet,
    SpecialCharacters,
    SpecialCharactersEssentials,
    SpecialCharactersExtended,
    Strikethrough,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TodoList,
    FigureTools, // Must come after Image, MediaEmbed and Table
    // Do not use the Title plugin.  It requires that all HTML pages have a Title,
    // and they are optional for AK
    Typing,
    Underline,
    Undo,
  ];

  const ckeditorLicenseKey = Messenger.reqres.request("get:ckeditor:licenseKey");
  const ckeditorTokenUrl = Messenger.reqres.request("get:ckeditor:tokenUrl");
  const wproofreaderServiceId = Messenger.reqres.request("get:wproofreader:serviceId");

  if (ckeditorLicenseKey) {
    plugins.push(PasteFromOfficeEnhanced);
    if (ckeditorTokenUrl) {
      plugins.push(CloudServices);
      plugins.push(ExportPdf);
      plugins.push(ImportWord);
      if (!options.skipCollaborationFeatures) {
        plugins.push(RealTimeCollaborativeEditing);
        plugins.push(AkCommentsAdapter);
        plugins.push(Comments);
        plugins.push(RealTimeCollaborativeComments);
        plugins.push(TrackChanges);
        plugins.push(RealTimeCollaborativeTrackChanges);
        plugins.push(RevisionHistory);
        plugins.push(RealTimeCollaborativeRevisionHistory);
        plugins.push(Autosave);
        plugins.push(PresenceList);
      }
    }
  }
  if (options.allowAiFeatures) {
    if (!options.skipResponseTransform) {
      plugins.push(ResponseTransformation);
      plugins.push(SummarizeResponse);
    }
    if (!options.skipExpandWithContext) {
      plugins.push(ExpandResponseWithContext);
    }
  }
  if (options.allowEditSource) {
    plugins.push(SourceEditing);
  }
  if (wproofreaderServiceId) {
    plugins.push(WProofreader);
  }
  return plugins;
}

function prepareDefaultConfig(options) {
  if (!options.allCategories) {
    options.allCategories = Messenger.reqres.request("kb:categories");
  }
  if (!options.allSnippets) {
    options.allSnippets = Messenger.reqres.request("kb:snippets");
  }
  // New customizations of plug-ins also need to be added to scripts/uploadEditorBundle.mjs
  let toolbarItems = ["undo", "redo", "findAndReplace", "selectAll", "|", "link", "bookmark", "|"];
  if (!options.skipImage) {
    toolbarItems.push("insertImage");
  }
  if (!options.skipConversationVariables) {
    toolbarItems.push("conversationVariable");
  }
  if (!options.skipAttributes) {
    toolbarItems.push("responseAttribute");
  }
  if (!options.skipCategories) {
    toolbarItems.push("responseCategory");
  }
  if (!options.skipSnippets) {
    toolbarItems.push("snippet");
  }
  toolbarItems = toolbarItems.concat([
    "insertTable",
    "privilegedArea",
    "mediaEmbed",
    "|",
    "horizontalLine",
    "specialCharacters",
    "pageBreak",
    /*'iframe', */ "|",
    "showBlocks",
    "|",
  ]);
  if (options.allowAiFeatures && options.aiOperations) {
    toolbarItems = toolbarItems.concat(["ai", "|"]);
  }
  if (options.allowEditSource) {
    toolbarItems = toolbarItems.concat(["sourceEditing", "|"]);
  }
  toolbarItems = toolbarItems.concat([
    "preview",
    "customTemplate",
    "|",
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "subscript",
    "superscript",
    "|",
    "removeFormat",
    "|",
    "numberedList",
    "bulletedList",
    "todoList",
    "|",
    "outdent",
    "indent",
    "|",
    "blockQuote",
    /*'createDiv',*/ "|",
    "alignment:left",
    "alignment:center",
    "alignment:right",
    "alignment:justify",
    "|",
    /*'styles', 'format',*/ "heading",
    "fontFamily",
    "fontSize",
    "|",
    "fontColor",
    "fontBackgroundColor",
    "accordion",
    "collapsibleSection",
  ]);
  let imageToolbar = [
    "resizeImage:original",
    "resizeImage:25",
    "resizeImage:50",
    "resizeImage:75",
    "|",
    "alignFigure:left",
    "alignFigure:center",
    "alignFigure:right",
    "|",
    "imageTextAlternative",
    "toggleImageCaption",
    "link",
  ];
  let mediaToolbar = [
    "resizeFigure:original",
    "resizeFigure:small",
    "resizeFigure:medium",
    "resizeFigure:large",
    "|",
    "alignFigure:left",
    "alignFigure:center",
    "alignFigure:right",
  ];
  const privilegedAreaRoleCollection = Messenger.reqres.request("kb:privileged:area:roles");
  const privilegedAreaRoles = {};
  privilegedAreaRoleCollection.forEach(function (privilegedArea) {
    privilegedAreaRoles[privilegedArea.get("roleId").toString()] = privilegedArea.get("roleName");
  });

  const licenseKey = Messenger.reqres.request("get:ckeditor:licenseKey");
  const tokenUrl = Messenger.reqres.request("get:ckeditor:tokenUrl");
  const webSocketUrl = Messenger.reqres.request("get:ckeditor:webSocketUrl");
  const wproofreaderServiceId = Messenger.reqres.request("get:wproofreader:serviceId");
  const wproofreaderSrcUrl = Messenger.reqres.request("get:wproofreader:srcUrl");

  if (licenseKey && tokenUrl) {
    toolbarItems = toolbarItems.concat(["|", "exportPdf", "importWord"]);
  }
  if (wproofreaderServiceId) {
    toolbarItems = toolbarItems.concat(["|", "wproofreader"]);
  }

  const bundleVersion = Messenger.reqres.request("get:ckeditor:bundleVersion");
  let collaborationEnabled = false;
  let documentId = options.documentId;
  if (!options.skipCollaborationFeatures && licenseKey && tokenUrl && documentId) {
    collaborationEnabled = true;
    toolbarItems = toolbarItems.concat([
      "|",
      "trackChanges",
      "comment",
      "commentsArchive",
      "revisionHistory",
    ]);
    imageToolbar = imageToolbar.concat(["|", "comment"]);
    mediaToolbar = mediaToolbar.concat(["|", "comment"]);
  }
  // TODO: Make sure to update the configuration in uploadEditorBundle.mjs whenever
  // changes are made to this configuration.
  // noinspection HtmlDeprecatedAttribute
  const config = {
    accordion: {
      bodyName: i18next.t("ckeditor.accordion.bodyName"),
      bodyPlaceholder: i18next.t("ckeditor.accordion.bodyPlaceholder"),
      headerName: i18next.t("ckeditor.accordion.headerName"),
      headerPlaceholder: i18next.t("ckeditor.accordion.headerPlaceholder"),
      toolbarName: i18next.t("ckeditor.accordion.widgetToolbar"),
      widgetName: i18next.t("ckeditor.accordion.widgetName"),
    },
    ai: {
      operations: options.aiOperations,
    },
    ak: {
      kbId: Messenger.reqres.request("active:kb"),
      ...(!options.skipExpandWithContext
        ? { intentId: Messenger.reqres.request("current:topic:id") }
        : {}),
    },
    collaborationEnabled: collaborationEnabled,
    collapsibleSection: {
      bodyName: i18next.t("ckeditor.collapsibleSection.bodyName"),
      bodyPlaceholder: i18next.t("ckeditor.collapsibleSection.bodyPlaceholder"),
      headerName: i18next.t("ckeditor.collapsibleSection.headerName"),
      headerPlaceholder: i18next.t("ckeditor.collapsibleSection.headerPlaceholder"),
      widgetName: i18next.t("ckeditor.collapsibleSection.widgetName"),
    },
    conversationVariable: {
      emptyVariableName: i18next.t("ckeditor.conversationVariable.emptyVariable"),
    },
    snippet: {
      emptyName: i18next.t("ckeditor.snippet.emptySnippet"),
      blockedId: options.snippetIdToBlockFromInsert,
      skipOpenSnippetEditor: options.skipOpenSnippetEditor,
      toolbarName: i18next.t("ckeditor.snippet.toolbar"),
    },
    fontSize: {
      options: ["default", 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72],
      // Allow all font sizes from Microsoft Office documents,
      // including those that are unknown to CKEditor.
      supportAllValues: true,
    },
    figureTools: {
      align: {
        // Defining custom styling options for the images.
        options: [
          {
            name: "left",
            icon: IconObjectLeft,
            title: i18next.t("ckeditor.align.left"),
          },
          {
            name: "right",
            icon: IconObjectRight,
            title: i18next.t("ckeditor.align.right"),
          },
          {
            name: "center",
            icon: IconObjectCenter,
            title: i18next.t("ckeditor.align.center"),
          },
        ],
      },
      resize: {
        unit: "px",
        options: [
          {
            name: "resizeFigure:small",
            value: 280,
            icon: "small",
            label: i18next.t("ckeditor.resize.small"),
          },
          {
            name: "resizeFigure:medium",
            value: 480,
            icon: "medium",
            label: i18next.t("ckeditor.resize.medium"),
          },
          {
            name: "resizeFigure:large",
            value: 900,
            icon: "large",
            label: i18next.t("ckeditor.resize.large"),
          },
          {
            name: "resizeFigure:original",
            value: null,
            icon: "original",
            label: i18next.t("ckeditor.resize.original"),
          },
        ],
      },
    },
    sourceEditing: {
      allowCollaborationFeatures: true,
    },
    mediaEmbed: {
      previewsInData: true,
      extraProviders: [
        {
          name: "brightcove",
          url: /^(?:https?:\/\/)?players\.brightcove\.net\/.*$/,
          html: (match) =>
            '<div style="position: relative; height: 0; padding-bottom: 56.2493%;">' +
            `<iframe src="${match[0]}" ` +
            'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
            'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
            "</iframe>" +
            "</div>",
        },
      ],
      toolbar: mediaToolbar,
    },
    heading: {
      options: [
        {
          model: "paragraph",
          title: i18next.t("ckeditor.heading.paragraph"),
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: i18next.t("ckeditor.heading.heading1"),
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: i18next.t("ckeditor.heading.heading2"),
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: i18next.t("ckeditor.heading.heading3"),
          class: "ck-heading_heading3",
        },
        {
          model: "heading4",
          view: "h4",
          title: i18next.t("ckeditor.heading.heading4"),
          class: "ck-heading_heading4",
        },
        {
          model: "heading5",
          view: "h5",
          title: i18next.t("ckeditor.heading.heading5"),
          class: "ck-heading_heading5",
        },
        {
          model: "heading6",
          view: "h6",
          title: i18next.t("ckeditor.heading.heading6"),
          class: "ck-heading_heading6",
        },
      ],
    },
    htmlSupport: {
      allow: [
        {
          name: /(audio|iframe|source|video)/,
          attributes: true,
          classes: true,
          styles: true,
        },
        {
          name: "div",
          attributes: true,
          classes: true,
          styles: true,
        },
        {
          name: "img",
          // height and width are handled by the Image plugin
          attributes: /(srcset|sizes)/,
          classes: true,
          styles: true,
        },
        {
          name: "table",
          attributes: /(cellpadding|cellspacing|border)/,
          styles: true,
          classes: true,
        },
        {
          name: /.*/,
          attributes:
            /(abbr|align|aria-.*|data-.*|dir|height|id|media|name|referrerpolicy|rel|role|scope|title|type|width)/,
          classes: true,
          styles: true,
        },
      ],
      disallow: [
        // Do not allow these attributes to be handled by general HTML, otherwise they won't
        // be properly handled by the AkLinkEditing and AkLinkImageEditing plug-ins
        {
          name: "a",
          attributes: /data-intent|data-intent-label|data-rdcontent|data-rdcontent-name/,
        },
        // Do not allow general HTML support for details and summary, because they are handled
        // by the collapsible panel plug-in
        {
          name: /details|summary/,
        },
        {
          name: /.*/,
          attributes: /on.*/,
        },
      ],
    },
    image: {
      // Explicitly set the insert type to "inline" so that images can be properly inserted
      // into Accordion headers
      insert: {
        type: "inline",
      },
      toolbar: imageToolbar,
    },
    language: Messenger.reqres.request("get:interface:language"),
    link: {
      allowedProtocols: ["https?", "mailto", "sms", "tel"],
      defaultProtocol: Properties.linkDefaultProtocol,
      decorators: {
        openInNewTab: {
          mode: "manual",
          label: i18next.t("ckeditor.linkTarget.labels.openInNewTab"),
          defaultValue: true,
          attributes: {
            target: LINK_TARGET_BLANK,
            rel: LINK_REL_SAFE,
          },
        },
      },
      topicLink: {
        unknownLabel: i18next.t("ckeditor.topicLink.unknown"),
        skip: options.skipTopicLink,
      },
      contentMappingLink: {
        skip: options.skipContentMappingLink,
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    privilegedArea: {
      invalidRoleName: i18next.t("privilegedArea.invalidRole"),
      placeholderName: i18next.t("ckeditor.privilegedArea.placeholder"),
      roles: privilegedAreaRoles,
    },
    responseAttribute: {
      emptyAttributeName: i18next.t("ckeditor.attribute.emptyVariable"),
    },
    responseCategory: {
      emptyCategoryName: i18next.t("ckeditor.category.emptyVariable"),
    },
    table: {
      tableToolbar: ["alignFigure:left", "alignFigure:center", "alignFigure:right"],
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableProperties",
        "tableCellProperties",
        "toggleTableCaption",
      ],
      tableProperties: {
        defaultProperties: {
          borderStyle: "solid",
          borderWidth: "1px",
          borderColor: "black",
        },
      },
      tableCellProperties: {
        defaultProperties: {
          borderStyle: "solid",
          borderWidth: "1px",
          borderColor: "black",
        },
      },
    },
    translations: [
      coreTranslationsDe,
      coreTranslationsEn,
      coreTranslationsEs,
      coreTranslationsFr,
      coreTranslationsZhCn,
      premiumFeaturesTranslationsDe,
      premiumFeaturesTranslationsEn,
      premiumFeaturesTranslationsEs,
      premiumFeaturesTranslationsFr,
      premiumFeaturesTranslationsZhCn,
    ],
    toolbar: {
      items: toolbarItems,
      shouldNotGroupWhenFull: true,
    },
  };

  if (collaborationEnabled) {
    if (typeof documentId != "string") {
      documentId = documentId.toString();
    }
    config.collaboration = {
      channelId: documentId,
    };
    config.autosave = {
      save: async (editor) => {
        if (editor.plugins.has("RevisionTracker")) {
          const revisionTracker = editor.plugins.get("RevisionTracker");
          // Update the current revision with the newest document changes.
          await revisionTracker.update();
        }
      },
    };
  } else {
    // Response translation, quick create topic response and variable prompts area do not use
    // collaboration features, and hence we need to set the CKEditor initial data.
    config.initialData = options.response;
  }
  if (licenseKey) {
    config.licenseKey = licenseKey;
    if (tokenUrl) {
      // noinspection JSUnusedGlobalSymbols
      config.exportPdf = {
        fileName: "topic.pdf",
        dataCallback: (editor) =>
          editor.getData({
            showSuggestionHighlights: true,
          }),
      };
      config.cloudServices = {
        bundleVersion: bundleVersion,
        tokenUrl: tokenUrl,
        webSocketUrl: webSocketUrl,
      };
    }
  }
  if (wproofreaderServiceId) {
    const locale = Messenger.reqres.request("get:interface:langAndLocale", options.languageId);
    config.wproofreader = {
      lang: locale,
      serviceId: wproofreaderServiceId,
      srcUrl: wproofreaderSrcUrl,
      autocorrect: false,
      theme: "ckeditor5",
    };
  }
  return config;
}

// noinspection JSUnusedGlobalSymbols
export default CKEditor5Module;
